/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.
import exam from '../../../share/util/inputValidator';
import alert from "../../../share/alert";
import logWrapper from "../../../share/logWrapper";
import { GraphMediator } from "../../../share/util/GraphLibrary";
import apiService from '../../../share/util/api/service';
import handler from '../../../share/util/apiResultValidator';
import { mapState } from "vuex";
const { v4: uuid } = require('uuid');
import ws45Utils from "../individualWorkSupport/requirement045/api/Requirement045Utils";
import diagnoseResource from "../../../share/util/diagnose";
import util from '../../../share/util/utils';

// fetch実行時に無応答と判定するまでの時間(ミリ秒)
const TIMEOUT_TIME = 30000;
// intdashサーバのプロジェクトUUID
const INTDASH_PROJECT_UUID = "00000000-0000-0000-0000-000000000000";
// コメントの文字数
const COMMENT_MAX_LEN = 500;
const ERR_MSG_KEY = {
  MARKER_SET: "marker_set",
  FETCH: "fetch",
};

export default {
  name: 'markPage',
  props: {
    apiToken: { type: String, default: "", required: true, },
    measurementUuid: { type: String, default: "", required: true, },
    elapsedTime: { type: Number, default: 0, required: true, },
    markerUuid: { type: String, default: "", required: true, },
    endpoint: { type: String, default: "", required: true, },
    userId: { type: String, default: "", required: true, },
    ws45MarkerInfo: { type: Object, default: () => null, required: false }, // WS.45 と WS.125 のマーカ情報
    modalMark: { type: Boolean, required: true, },
    loadMark: { type: Number, default: 0, required: true }, // マーカ登録画面の読込判定
    // トレーニングモード用
    commentForTestMode: { type: String, default: "", required: false, }, // コメント
    elapsedTimeForTestMode: { type: Number, default: 0, required: false, }, // 経過時間
    tagForTestMode: { type: Object, default: null, required: false, }, // タグ情報
    isTestMode: { type: Boolean, default: false, required: false }, // トレーニングモードか否か
  },

  data() {
    return {
      modalLoading: false,
      isAlert: {},
      comment: "",
      time: "",
      registeredUserId: "",
      isSharedChecked: true,
      timeoutTimer: null
    };
  },

  computed: {
    ...mapState({
      cachedProjectDataSource: state => state.cache.projectDataSource,
    }),
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    },
  },

  watch: {
    loadMark: {
      handler() {
        this.clearAlert();
        this.getInfo();
      }
    }
  },

  methods: {
    // マーカ情報の保存
    async save() {
      // トレーニングモード時はサーバへの登録要求は行わない
      if (this.isTestMode) {
        return this.createTestModeData(this.elapsedTime);
      }

      let ret = null;
      // 入力の検証
      if (!this.validate()) return ret;
      // API呼び出し
      const resultResponse = await this.createMeasurementMarker(this.userId, this.isSharedChecked);
      if (resultResponse && resultResponse.uuid) {
        ret = {
          uuid: resultResponse.uuid,
          time: Math.floor(resultResponse.detail.occurred_elapsed_time / 1000),
          comment: resultResponse.description,
          tag: resultResponse.tag
        };
      }
      else if (resultResponse && resultResponse.error) {
        ret = { error: resultResponse.error };
      }
      return ret;
    },

    /**
     * 汎用のマーカ情報の保存
     * @param {String} comment マーカー登録情報
     * @returns {Object} マーカ登録実行APIのレスポンス
     */
    async externalSaveMarker(comment) {
      // トレーニングモード時はサーバへの登録要求は行わない
      if (this.isTestMode) {
        return false;
      }

      this.comment = comment;

      // API呼び出し
      return await this.createMeasurementMarker(this.userId, this.isSharedChecked);
    },

    // マーカ情報のクリア
    async clear() {
      this.comment = "";
      this.time = "";

      // トレーニングモード時はサーバへの削除要求は行わない
      if (this.isTestMode) {
        return {};
      }

      // API呼び出し
      return await this.deleteMeasurementMarker();
    },

    validate() {
      // 検証結果キャッシュの初期化
      this.clearAlert();
      const resultPool = [];

      // メモの文字数チェック
      this.isAlert.comment = exam.excuteValidateCommons(this.comment, false, 0, COMMENT_MAX_LEN);
      resultPool.push(this.isAlert.comment.valid);

      const faults = resultPool.filter(el => el === false);
      return faults.length === 0;
    },

    // 入力検証アラートのクリア
    clearAlert: function () {
      this.isAlert = {
        comment: { valid: null, msg: '' },
      };
    },

    /**
     * トレーニングモード用のダミーデータを作成する
     * @param {Number} elapsedTime 経過時間
     * @returns {Object} ダミーデータ
     */
    createTestModeData(elapsedTime) {
      return {
        uuid: this.markerUuid === "" ? uuid() : this.markerUuid,
        time: elapsedTime,
        comment: this.comment,
        tag: { userId: this.userId, shared: String(this.isSharedChecked) }
      };
    },

    // マーカ情報更新
    async update() {
      // トレーニングモード時はサーバへの更新要求は行わない
      if (this.isTestMode) {
        return this.createTestModeData(this.elapsedTimeForTestMode);
      }

      let ret = null;
      // 入力の検証
      if (!this.validate()) return ret;
      // API呼び出し
      const resultResponse = await this.replaceMeasurementMarker(this.isSharedChecked);
      if (resultResponse && resultResponse.uuid) {
        ret = {
          uuid: resultResponse.uuid,
          time: Math.floor(resultResponse.detail.occurred_elapsed_time / 1000),
          comment: resultResponse.description,
          tag: resultResponse.tag
        };
      }
      else if (resultResponse && resultResponse.error) {
        ret = { error: resultResponse.error };
      }
      return ret;
    },

    // マーカ情報取得
    async getInfo() {
      // 登録と更新の判定（markerUuidが存在する場合は更新）
      if (this.markerUuid !== "") {
        // トレーニングモードの場合は呼び出し元の情報をそのまま表示
        if (this.isTestMode) {
          this.comment = this.commentForTestMode;
          this.time = GraphMediator.getPassedTimeStr(this.elapsedTimeForTestMode, true);
          this.registeredUserId = this.tagForTestMode.userId;
          this.isSharedChecked = this.tagForTestMode.shared.toLowerCase() === 'true';
          return;
        }

        // API呼び出し
        await this.getMeasurementMarker();
      } else {
        this.comment = "";
        this.time = GraphMediator.getPassedTimeStr(this.elapsedTime, true);
        this.isSharedChecked = true;
        this.registeredUserId = "";
      }
    },

    /**
     * 指定した計測UUIDに紐づけて、計測マーカーを作成する
     * @param {String} userId ログインユーザーのID
     * @param {Boolean} isShared 他ユーザーに共有するか否か
     * @returns PostのResponse
     */
    async createMeasurementMarker(userId, isShared) {
      try {
        if (this.modalMark) {
          await this.setModalLoading(true);
        }
        // WS.45/125から呼び出された場合はAPIが異なる
        if (this.ws45MarkerInfo) {
          return await this.handleWs45GraphMarker(this.elapsedTime, 0, this.comment, isShared, userId);
        }

        if (!this.endpoint) {
          // intdashのendpoint未指定の場合、SSM5のマーカーAPI使用
          const markerId = uuid();
          const body = {
            marker_time: Math.floor(this.elapsedTime * 1000),
            comment: this.comment,
            is_shared: String(isShared),
            user_id: userId,
            is_update: false,
            region_info: this.cachedProjectDataSource
          };
          const response = await apiService.postCall(apiService.DMAT, `/measurements/${this.measurementUuid}/markers/${markerId}`, body);
          let resultResponse = null;
          handler.validate(handler.validateTypes.all, response, this, null, () => {
            resultResponse = response.data;
          }, (failResult) => {
            console.warn(failResult);
            this.showUnexpectedError(ERR_MSG_KEY.MARKER_SET);
            return;
          });
          return resultResponse;
        }

        const data = {
          name: "name",
          description: this.comment,
          type: "point",
          detail: {
            "occurred_elapsed_time": Math.floor(this.elapsedTime * 1000) // マイクロ秒
          },
          tag: { userId: userId, shared: String(isShared) }
        };

        const response = await fetch(
          "https://" +
          this.endpoint +
          "/api/" +
          "v1/projects/" +
          INTDASH_PROJECT_UUID +
          "/measurements/" +
          this.measurementUuid +
          "/markers",
          this.getRequestOptions("POST", data)
        ).then((res) => {
          if (!res.ok || res.status !== 201) {
            throw new Error(`${res.status} ${res.statusText}`);
          } else {
            return res;
          }
        }).then(
          checked_res => checked_res.json()
        ).catch((error) => {
          logWrapper.log(`createMeasurementMarker fetch error: ${error}`);
          if (error.name === "AbortError") {
            return {
              error: ERR_MSG_KEY.FETCH
            };
          }
          this.showUnexpectedError(ERR_MSG_KEY.MARKER_SET);
          return null;
        });

        const resultResponse = await response;

        return resultResponse;
      } catch (e) {
        logWrapper.log(`createMeasurementMarker error: ${e}`);
        return {
          error: "CreateMarkerError",
        };
      } finally {
        await this.setModalLoading(false);
        if (this.timeoutTimer) {
          clearTimeout(this.timeoutTimer);
          this.timeoutTimer = null;
        }
      }
    },

    /**
     * 指定した計測マーカーの情報を更新する
     * @param {Boolean} isShared 他ユーザーに共有するか否か
     * @returns PutのResponses
     */
    async replaceMeasurementMarker(isShared) {
      try {
        await this.setModalLoading(true);
        // WS.45/125から呼び出された場合はAPIが異なる
        if (this.ws45MarkerInfo) {
          // 参照するmarkerTimeにbaseTimeが含まれているため、引数のbaseTimeはnullにする
          return await this.handleWs45GraphMarker(null, Number(this.markerUuid), this.comment, isShared, this.registeredUserId);
        }

        if (!this.endpoint) {
          // intdashのendpoint未指定の場合、SSM5のマーカーAPI使用
          const body = {
            marker_time: this.selectedMarkerTime,
            comment: this.comment,
            is_shared: String(isShared),
            user_id: this.registeredUserId,
            is_update: true,
            region_info: this.cachedProjectDataSource
          };
          const response = await apiService.postCall(apiService.DMAT, `/measurements/${this.measurementUuid}/markers/${this.markerUuid}`, body);
          let resultResponse = null;
          handler.validate(handler.validateTypes.all, response, this, null, () => {
            resultResponse = response.data;
          }, (failResult) => {
            console.warn(failResult);
            this.showUnexpectedError(ERR_MSG_KEY.MARKER_SET);
          });
          return resultResponse;
        }

        const data = {
          description: this.comment,
          tag: { userId: this.registeredUserId, shared: String(isShared) }
        };

        const response = await fetch(
          `https://${this.endpoint}/api/v1/projects/${INTDASH_PROJECT_UUID}/measurements/${this.measurementUuid}/markers/${this.markerUuid}`,
          this.getRequestOptions("PUT", data)
        ).then((res) => {
          if (!res.ok || res.status !== 200) {
            throw new Error(`${res.status} ${res.statusText}`);
          } else {
            return res;
          }
        }).then(
          checked_res => checked_res.json()
        ).catch((error) => {
          logWrapper.log(`replaceMeasurementMarker fetch error: ${error}`);
          if (error.name === "AbortError") {
            return {
              error: ERR_MSG_KEY.FETCH
            };
          }
          this.showUnexpectedError(ERR_MSG_KEY.MARKER_SET);
          return null;
        });

        const resultResponse = await response;

        return resultResponse;
      } catch (e) {
        logWrapper.log(`replaceMeasurementMarker error: ${e}`);
        return {
          error: "ReplaceMarkerError",
        };
      } finally {
        await this.setModalLoading(false);
        if (this.timeoutTimer) {
          clearTimeout(this.timeoutTimer);
          this.timeoutTimer = null;
        }
      }
    },

    /**
     * 指定した計測マーカーを削除する
     */
    async deleteMeasurementMarker() {
      try {
        await this.setModalLoading(true);
        // WS.45/125から呼び出された場合はAPIが異なる
        if (this.ws45MarkerInfo) {
          // コメントを null、共有フラグを false に設定して呼び出し
          return await this.handleWs45GraphMarker(null, Number(this.markerUuid), null, false, this.registeredUserId);
        }

        if (!this.endpoint) {
          // intdashのendpoint未指定の場合、SSM5のマーカーAPI使用
          const response = await apiService.deleteCall(apiService.DMAT, `/measurements/${this.measurementUuid}/markers/${this.markerUuid}?region_info=${this.cachedProjectDataSource}`);
          let resultResponse = null;
          handler.validate(handler.validateTypes.all, response, this, null, () => {
            resultResponse = response;
          }, (failResult) => {
            console.warn(failResult);
            this.showUnexpectedError(ERR_MSG_KEY.MARKER_SET);
            resultResponse = { error: failResult };
          });
          return resultResponse;
        }

        return await fetch(
          `https://${this.endpoint}/api/v1/projects/${INTDASH_PROJECT_UUID}/measurements/${this.measurementUuid}/markers/${this.markerUuid}`,
          this.getRequestOptions("DELETE")
        ).then((res) => {
          if (!res.ok || res.status !== 204) {
            throw new Error(`${res.status} ${res.statusText}`);
          } else {
            return res;
          }
        }
        ).catch((error) => {
          logWrapper.log(`deleteMeasurementMarker fetch error: ${error}`);
          if (error.name === "AbortError") {
            this.showUnexpectedError(ERR_MSG_KEY.FETCH);
          } else {
            this.showUnexpectedError(ERR_MSG_KEY.MARKER_SET);
          }
          return { error: error };
        });
      } catch (e) {
        logWrapper.log(`deleteMeasurementMarker error: ${e}`);
        return {
          error: "DeleteMarkerError",
        };
      } finally {
        await this.setModalLoading(false);
        if (this.timeoutTimer) {
          clearTimeout(this.timeoutTimer);
          this.timeoutTimer = null;
        }
      }
    },

    /**
     * 指定した計測マーカーの情報を取得する
     * @returns GetのResponse
     */
    async getMeasurementMarker() {
      try {
        await this.setModalLoading(true);

        let resultResponse;

        // WS.45の場合はAPIが異なる
        if (this.ws45MarkerInfo) {
          resultResponse = await ws45Utils.getGraphMarker(
            this, this.measurementUuid, this.ws45MarkerInfo, this.markerUuid, this.cachedProjectDataSource
          );
          // マーカー情報が存在しない場合、エラー画面を表示
          if (resultResponse?.error) {
            this.showUnexpectedError(ERR_MSG_KEY.MARKER_SET);
            return resultResponse;
          }
        } else if (!this.endpoint) {
          // intdashのendpoint未指定の場合、SSM5のマーカーAPI使用
          const response = await apiService.getCall(apiService.DMAT, `/measurements/${this.measurementUuid}/markers/${this.markerUuid}?region_info=${this.cachedProjectDataSource}`);
          handler.validate(handler.validateTypes.all, response, this, null, () => {
            resultResponse = response.data;
          }, (failResult) => {
            console.warn(failResult);
            this.showUnexpectedError(ERR_MSG_KEY.MARKER_SET);
          });
        } else {
          // IntdashのAPI呼び出し
          const response = await fetch(
            `https://${this.endpoint}/api/v1/projects/${INTDASH_PROJECT_UUID}/measurements/${this.measurementUuid}/markers/${this.markerUuid}`,
            this.getRequestOptions("GET")
          ).then((res) => {
            if (!res.ok || res.status !== 200) {
              throw new Error(`${res.status} ${res.statusText}`);
            } else {
              return res;
            }
          }).then(
            checked_res => checked_res.json()
          ).catch((error) => {
            logWrapper.log(`getMeasurementMarker fetch error: ${error}`);
            if (error.name === "AbortError") {
              this.showUnexpectedError(ERR_MSG_KEY.FETCH);
            } else {
              this.showUnexpectedError(ERR_MSG_KEY.MARKER_SET);
            }
          });

          resultResponse = await response;
        }

        // 必要なデータをthisに設定
        if (resultResponse && resultResponse.uuid) {
          const shared = resultResponse.tag?.shared;
          this.comment = resultResponse.description;
          this.time = GraphMediator.getPassedTimeStr(Math.floor(resultResponse.detail.occurred_elapsed_time / 1000), true);
          this.registeredUserId = resultResponse.tag && resultResponse.tag.userId;
          this.isSharedChecked = typeof shared === 'string' ? shared.toLowerCase() === 'true' : shared;
          this.selectedMarkerTime = resultResponse.detail.occurred_elapsed_time;
        }

        return resultResponse;
      } catch (e) {
        logWrapper.log(`getMeasurementMarker error: ${e}`);
        return {
          error: "GetMarkerError",
        };
      } finally {
        // スピナー消去後、削除/保存ボタン活性化までにタイムラグを発生させる（_leaveCbエラー回避のため）
        await this.setModalLoading(false , 100);
        if (this.timeoutTimer) {
          clearTimeout(this.timeoutTimer);
          this.timeoutTimer = null;
        }
      }
    },

    /**
     * マーカ操作時のエラー
     */
    showUnexpectedError(action) {
      let messageKey = "";
      switch (action) {
        case ERR_MSG_KEY.FETCH:
          // メッセージは同一とする
          messageKey = "individual.error_data_monitor_marker_set";
          break;
        default:
          messageKey = "individual.error_data_monitor_marker_set";
          break;
      }
      alert.showError(this, null, this.$t(messageKey), () => { });
    },

    /**
     * HTTPリクエストの設定作成
     * @param {string} method HTTPメソッド
     * @param {object} body リクエストボディ
     * @returns {object} HTTPクライアントに渡す設定
     */
    getRequestOptions(method, body = null) {
      // バージョン103以前のEdgeは`AbortSignal.timeout`メソッド非対応のため、一定時間でAbortさせてタイムアウトさせる
      const controller = new AbortController();
      this.timeoutTimer = setTimeout(() => controller.abort(), TIMEOUT_TIME);

      const headers = new FormData();
      headers.append('X-Intdash-Token', this.apiToken);
      headers.append('Content-Type', 'application/json; charset=utf-8');
      const options = {
        signal: controller.signal,
        method: method,
        headers: headers,
      };
      return body ? { ...options, body: JSON.stringify(body) } : options;
    },

    /**
     * WS.45/125 のグラフマーカーを操作する共通関数
     * @param {Number} elapsedTime 経過時間 (更新および削除時はnullとする)
     * @param {Number} markerTime マーカーUUIDを数値化したもの
     * @param {String|null} comment コメント (削除時は null)
     * @param {Boolean} isShared 他ユーザーに共有するか (削除時は false)
     * @param {String} userId ユーザーID
     * @returns {Object} APIレスポンス
    */
    async handleWs45GraphMarker(elapsedTime, markerTime, comment, isShared, userId) {
      // 現在の画面で使用されるAPIが診断/データ再生/個別診断/OBDシステムのうちどの機能なのか判別する
      const operationScreen = this.ws45MarkerInfo.isPlay
        ? diagnoseResource.operationFrom(this.$router.currentRoute.value.name)
        : null; // 個別診断からの呼び出しの場合は、null
      const response = await ws45Utils.updateGraphMarker(
        this,
        this.measurementUuid,
        this.ws45MarkerInfo,
        elapsedTime,
        markerTime,
        comment,
        isShared,
        userId,
        operationScreen,
        this.cachedProjectDataSource
      );

      // response が不正の場合、マーカ設定エラーを表示
      if (response?.error) this.showUnexpectedError(ERR_MSG_KEY.MARKER_SET);

      return response;
    },

    /**
     * モーダルのローディング状態を設定する
     * @param {Boolean} bool ローディング状態
     * @param {Number} delay ローディング状態の変更後に待機する時間 (ms)
     */
    async setModalLoading(bool, delay = 0) {
      this.modalLoading = bool;
      if (delay > 0) {
        await util.sleep(delay);
      }
      // モーダルのローディング状態を親コンポーネントに通知する 
      this.$emit("set-mark-modal-loading", this.modalLoading);
    }
  },

  async created() {
    // noop
  },

  mounted() {
    // noop
  },
};